
import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.unobserve(el); } },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, visible };
}

function AnimatedFeatureRow({
  feature,
  dark = true,
  handleImageError,
}: {
  feature: { title: string; description: string; points: string[]; image: string; reverse: boolean };
  dark?: boolean;
  handleImageError: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}) {
  const imgRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [imgVisible, setImgVisible] = useState(false);
  const [textVisible, setTextVisible] = useState(false);

  useEffect(() => {
    const observe = (el: HTMLElement | null, setter: (v: boolean) => void) => {
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) { setter(true); obs.unobserve(el); } },
        { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
      );
      obs.observe(el);
      return obs;
    };
    const o1 = observe(imgRef.current, setImgVisible);
    const o2 = observe(textRef.current, setTextVisible);
    return () => { o1?.disconnect(); o2?.disconnect(); };
  }, []);

  const titleClass = dark ? 'text-white' : 'text-gray-900';
  const descClass = dark ? 'text-white/75' : 'text-gray-600';
  const pointClass = dark ? 'text-white' : 'text-gray-800';
  const imgBgClass = dark ? 'bg-white/10' : 'bg-gray-100';

  return (
    <div className={`flex flex-col ${feature.reverse ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-5 sm:gap-12`}>
      <div
        ref={imgRef}
        style={{ transitionDelay: '0ms' }}
        className={`w-full md:w-1/2 transition-all duration-700
          ${imgVisible
            ? 'opacity-100 translate-x-0'
            : feature.reverse ? 'opacity-0 translate-x-12' : 'opacity-0 -translate-x-12'}`}
      >
        <div className={`w-full h-48 sm:h-80 md:h-96 rounded-xl overflow-hidden shadow-lg ${imgBgClass} flex items-center justify-center`}>
          <img
            src={feature.image}
            alt={feature.title}
            onError={handleImageError}
            className="w-full h-full object-contain"
          />
        </div>
      </div>

      <div
        ref={textRef}
        style={{ transitionDelay: '150ms' }}
        className={`w-full md:w-1/2 transition-all duration-700
          ${textVisible
            ? 'opacity-100 translate-x-0'
            : feature.reverse ? 'opacity-0 -translate-x-12' : 'opacity-0 translate-x-12'}`}
      >
        <h3 className={`text-lg sm:text-3xl font-bold ${titleClass} mb-2 sm:mb-4`}>{feature.title}</h3>
        <p className={`text-xs sm:text-lg ${descClass} mb-3 sm:mb-6 leading-relaxed`}>{feature.description}</p>
        <ul className="space-y-1.5 sm:space-y-3">
          {feature.points.map((point, idx) => (
            <li key={idx} className={`flex items-center text-xs sm:text-base ${pointClass}`}>
              <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center mr-2 sm:mr-3">
                <i className="ri-checkbox-circle-fill text-sm sm:text-xl text-[#d6df00]"></i>
              </div>
              <span>{point}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function Features() {
  const { t } = useTranslation();

  const feature1 = {
    title: t('features.f1.title'),
    description: t('features.f1.desc'),
    points: [t('features.f1.p1'), t('features.f1.p2'), t('features.f1.p3')],
    image: 'https://cdn.unionlab.ai/FABRICA.png',
    reverse: false,
  };

  const feature2 = {
    title: t('features.f2.title'),
    description: t('features.f2.desc'),
    points: [t('features.f2.p1'), t('features.f2.p2')],
    image: 'https://static.readdy.ai/image/d4364c93ef6f707650170405d0b37024/de6bc692dd8f7c3a9dffe084ee8f69a6.jpeg',
    reverse: true,
  };

  const feature3 = {
    title: t('features.f3.title'),
    description: t('features.f3.desc'),
    points: [t('features.f3.p1'), t('features.f3.p2'), t('features.f3.p3')],
    image: 'https://cdn.unionlab.ai/hf_20260217_025308_d6b1651c-1cc1-4ccc-af75-098be4c53f28.png',
    reverse: false,
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.target as HTMLImageElement;
    target.src = 'https://via.placeholder.com/600x400?text=Imagen+no+disponible';
    target.alt = 'Imagen no disponible';
  };

  return (
    <>
      {/* Toda la sección — fondo blanco con ondas */}
      <section id="capacidades" className="py-12 sm:py-24 relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url('https://static.readdy.ai/image/d4364c93ef6f707650170405d0b37024/a93eb12971d30b4a360ecfa2661f1933.jpeg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
          aria-hidden="true"
        />

        <div className="max-w-7xl mx-auto px-5 sm:px-6 relative z-10">
          <div className="text-center mb-10 sm:mb-20">
            <p className="text-xs sm:text-sm font-semibold text-[#5a7a00] uppercase tracking-wide mb-2 sm:mb-3">
              {t('features.tag')}
            </p>
            <h2 className="text-[1.4rem] leading-tight sm:text-4xl md:text-5xl font-bold text-gray-900">
              {t('features.title')}
            </h2>
          </div>

          <div className="space-y-10 sm:space-y-24">
            <AnimatedFeatureRow feature={feature1} dark={false} handleImageError={handleImageError} />
            <AnimatedFeatureRow feature={feature2} dark={false} handleImageError={handleImageError} />
            <AnimatedFeatureRow feature={feature3} dark={false} handleImageError={handleImageError} />
          </div>
        </div>
      </section>
    </>
  );
}
